import { ObjectSchema, object, string } from 'yup';
import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';

export type TransactionExportFormState = {
  startDate: string;
  endDate: string;
};

export const transactionExportFormStateDefault: TransactionExportFormState = {
  startDate: '',
  endDate: '',
};

export const transactionExportSchemaDefault: ObjectSchema<Partial<TransactionExportFormState>> =
  object<Partial<TransactionExportFormState>>().shape({
    startDate: string()
      .required('Start date is required field')
      .test('is-within-30-days', 'Start date must be within the last 30 days', (value) => {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);

        return selectedDate >= thirtyDaysAgo && selectedDate <= today;
      }),
    endDate: string()
      .required('End date is required field')
      .test('is-within-30-days', 'End date must be within the last 30 days', (value) => {
        if (!value) return true;
        const selectedDate = new Date(value);
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);

        return selectedDate >= thirtyDaysAgo && selectedDate <= today;
      })
      .test('is-after-start', 'End date must be later than start date', function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;

        // Parse datetime-local format strings (YYYY-MM-DDTHH:mm)
        const endDateTime = new Date(value.length === 16 ? value + ':00' : value);
        const startDateTime = new Date(startDate.length === 16 ? startDate + ':00' : startDate);

        return endDateTime.getTime() > startDateTime.getTime();
      }),
  });

type TransactionExportFormContextType = {
  schema: ObjectSchema<Partial<TransactionExportFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<TransactionExportFormState>>>;
  methods?: UseFormReturn<TransactionExportFormState>;
};

const transactionExportFormContextDefault = {
  schema: transactionExportSchemaDefault,
  setSchema: noop,
} satisfies TransactionExportFormContextType;

export const TransactionExportFormContext = createContext<TransactionExportFormContextType>(
  transactionExportFormContextDefault
);

export const useTransactionExportForm = () => {
  const { methods } = useContext(TransactionExportFormContext);
  if (!methods) {
    throw new Error('TransactionExportFormContext is empty');
  }

  return methods;
};

export const useManageTransactionExportSchema = () => {
  const { setSchema } = useContext(TransactionExportFormContext);

  return { setSchema };
};
